import {
  APIReferenceAttributes,
  ArdoqId,
  Entity,
  ResourceType,
  WebSocketFieldUnappliedPayload,
  WebSocketGlobalFieldDeletedPayload,
  WebSocketGlobalFieldOptionsRemovedPayload,
} from '@ardoq/api-types';
import {
  getStreamCollectionInitialState,
  toStreamCollection,
} from 'streams/utils/streamUtils';
import {
  reducedStream,
  reducer,
  spreadFilter,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import { shouldProcessEventForBranch } from 'sync/ardoqEventOperations';
import * as referenceActions from './ReferenceActions';
import { filterResourceType, websocket$ } from 'sync/websocket$';
import { websocketReducer } from 'streams/crud/reducers';
import {
  ArdoqEvent,
  isWebSocketFieldUnapplied,
  isWebSocketGlobalFieldDeleted,
  isWebSocketGlobalFieldOptionsRemoved,
} from 'sync/types';
import { fieldOps } from 'models/utils/fieldOps';

const clearScenarioRelatedIds = (_state: Set<ArdoqId>): Set<ArdoqId> =>
  new Set();

const setScenarioRelatedIds = (
  _state: Set<ArdoqId>,
  ids: ArdoqId[]
): Set<ArdoqId> => new Set(ids);

export const scenarioRelatedIds$ = reducedStream<Set<ArdoqId>>(
  'scenarioRelatedIds$',
  new Set(),
  [
    reducer(referenceActions.resetReferences, clearScenarioRelatedIds),
    reducer(referenceActions.resetReferencesWithData, clearScenarioRelatedIds),
    reducer(
      referenceActions.setScenarioRelatedReferenceIds,
      setScenarioRelatedIds
    ),
  ]
);

export const addReferences = (
  state: APIReferenceAttributes[],
  refs: APIReferenceAttributes[]
): APIReferenceAttributes[] => [...state, ...refs];

export const removeReferences = (
  state: APIReferenceAttributes[],
  ids: ArdoqId[]
): APIReferenceAttributes[] => {
  const idSet = new Set(ids);
  return state.filter(ref => !idSet.has(ref._id));
};

export const updateReference = (
  state: APIReferenceAttributes[],
  updated: Partial<APIReferenceAttributes> & Entity
): APIReferenceAttributes[] =>
  state.map(ref => (ref._id === updated._id ? { ...ref, ...updated } : ref));

const unsetAttributeOnReference = (
  ref: APIReferenceAttributes,
  attribute: string
): APIReferenceAttributes => ({
  ...ref,
  [attribute]: undefined,
  _version: ref._version + 1,
});

const addReferencesReducer = (
  state: APIReferenceAttributes[],
  { references }: referenceActions.AddReferencesPayload
): APIReferenceAttributes[] => {
  const incomingById = new Map(references.map(ref => [ref._id, ref]));
  const merged = state.map(ref => {
    const incoming = incomingById.get(ref._id);
    if (!incoming) return ref;
    incomingById.delete(ref._id);
    return { ...ref, ...incoming };
  });
  return [...merged, ...incomingById.values()];
};

const resetReferencesReducer = (
  _state: APIReferenceAttributes[]
): APIReferenceAttributes[] => [];

const replaceReferencesReducer = (
  _state: APIReferenceAttributes[],
  references: APIReferenceAttributes[]
): APIReferenceAttributes[] => references;

const removeReferencesReducer = (
  state: APIReferenceAttributes[],
  { ids }: referenceActions.RemoveReferencesPayload
): APIReferenceAttributes[] => removeReferences(state, ids);

const deleteReferenceReducer = (
  state: APIReferenceAttributes[],
  id: ArdoqId
): APIReferenceAttributes[] => removeReferences(state, [id]);

const removeGlobalFieldOptionsReducer = (
  state: APIReferenceAttributes[],
  {
    data: { name, options },
  }: ArdoqEvent<WebSocketGlobalFieldOptionsRemovedPayload>
): APIReferenceAttributes[] => {
  const removed = new Set(options);
  return state.map(ref => {
    const value = ref[name];
    if (value === undefined) return ref;
    const kept = fieldOps.withoutOptions(value, removed);
    if (kept === value) return ref;
    return { ...ref, [name]: kept, _version: ref._version + 1 };
  });
};

const unsetUnappliedFieldReducer = (
  state: APIReferenceAttributes[],
  { data }: ArdoqEvent<WebSocketFieldUnappliedPayload>
): APIReferenceAttributes[] => {
  const types = new Set(data.referenceType);
  return state.map(ref => {
    if (ref[data.name] === undefined) return ref;
    if (ref.model !== data.model) return ref;
    if (!types.has(String(ref.type))) return ref;
    return unsetAttributeOnReference(ref, data.name);
  });
};

const unsetDeletedGlobalFieldReducer = (
  state: APIReferenceAttributes[],
  { data }: ArdoqEvent<WebSocketGlobalFieldDeletedPayload>
): APIReferenceAttributes[] =>
  state.map(ref =>
    ref[data.name] === undefined
      ? ref
      : unsetAttributeOnReference(ref, data.name)
  );

const referencesOnly$ = reducedStream<APIReferenceAttributes[]>(
  'referencesOnly$',
  [],
  [
    reducer(referenceActions.addReferences, addReferencesReducer),
    reducer(referenceActions.resetReferences, resetReferencesReducer),
    reducer(referenceActions.resetReferencesWithData, replaceReferencesReducer),
    reducer(referenceActions.removeReferences, removeReferencesReducer),
    reducer(referenceActions.updateReference, updateReference),
    streamReducer(
      websocket$.pipe(
        filterResourceType<APIReferenceAttributes>(ResourceType.REFERENCE),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      websocketReducer({ delete: deleteReferenceReducer })
    ),
    streamReducer(
      websocket$.pipe(filter(isWebSocketGlobalFieldDeleted)),
      unsetDeletedGlobalFieldReducer
    ),
    streamReducer(
      websocket$.pipe(
        filter(isWebSocketGlobalFieldOptionsRemoved),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      removeGlobalFieldOptionsReducer
    ),
    streamReducer(
      websocket$.pipe(
        filter(isWebSocketFieldUnapplied),
        withLatestFrom(activeBranchId$),
        spreadFilter(shouldProcessEventForBranch)
      ),
      unsetUnappliedFieldReducer
    ),
  ]
);

export const references$ = toPersistentStream(
  'references$',
  referencesOnly$.pipe(map(toStreamCollection)),
  getStreamCollectionInitialState()
);
